import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { FirebaseMessaging, type PermissionState } from '@capacitor-firebase/messaging'
import { Preferences } from '@capacitor/preferences'
import { getPushAppLocale } from '~/utils/pushDestination'

export type PushPermissionStatus = PermissionState | 'not_determined' | 'restricted' | 'provisional'

export type SetNotificationsResult =
  | 'enabled'
  | 'disabled'
  | 'permission_denied'
  | 'sync_failed'
  | 'unsupported'

export interface PushCategoryPreference {
  id: number
  key: string
  label: string
  description?: string | null
  is_system: boolean
  enabled: boolean
  android_channel_id?: string | null
  default_enabled?: boolean
}

export interface PushPreferencesResponse {
  push_opt_in: boolean
  push_permission_status: PushPermissionStatus
  categories: PushCategoryPreference[]
}

const INSTALLATION_UUID_KEY = 'push_installation_uuid'
const FCM_TOKEN_KEY = 'push_fcm_token'

const DEFAULT_ANDROID_CHANNELS = [
  {
    id: 'glouton_default',
    name: 'Glouton',
    description: 'Notifications générales Glouton',
    importance: 3,
  },
  {
    id: 'glouton_circulaires',
    name: 'Disponibilité des circulaires',
    description: 'Soyez avisé dès que nos analyses des rabais sont disponibles.',
    importance: 3,
  },
  {
    id: 'glouton_aubaines_importantes',
    name: 'Aubaines à ne pas manquer',
    description: 'Meilleures aubaines et alertes sur vos produits suivis.',
    importance: 4,
  },
  {
    id: 'glouton_infos',
    name: 'À propos de Glouton',
    description: 'Nouvelles Glouton, partenariats, annonces et Extra.',
    importance: 3,
  },
  {
    id: 'glouton_systeme',
    name: 'Système',
    description: 'Communications essentielles liées à votre compte.',
    importance: 4,
  },
] as const

const OS_VERSION_MAX_LENGTH = 64
const DEVICE_MODEL_MAX_LENGTH = 128

type ApiPushPermissionStatus = 'not_determined' | 'granted' | 'denied' | 'provisional' | 'restricted'

function truncateString(value: string, maxLength: number): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value
}

function extractOsVersionFromUserAgent(userAgent: string): string {
  const androidMatch = userAgent.match(/Android (\d+(?:\.\d+)?)/)

  if (androidMatch) {
    return truncateString(`Android ${androidMatch[1]}`, OS_VERSION_MAX_LENGTH)
  }

  const iosMatch = userAgent.match(/OS (\d+(?:[_\d]*)?) like Mac OS X/)

  if (iosMatch) {
    const version = iosMatch[1].replace(/_/g, '.')

    return truncateString(`iOS ${version}`, OS_VERSION_MAX_LENGTH)
  }

  return truncateString(userAgent, OS_VERSION_MAX_LENGTH)
}

function normalizePermissionStatusForApi(status: PushPermissionStatus | string): ApiPushPermissionStatus {
  if (status === 'prompt' || status === 'prompt-with-rationale') {
    return 'not_determined'
  }

  if (
    status === 'granted'
    || status === 'denied'
    || status === 'provisional'
    || status === 'restricted'
    || status === 'not_determined'
  ) {
    return status
  }

  return 'not_determined'
}

function getFetchErrorDetails(error: unknown): unknown {
  if (typeof error !== 'object' || error === null) {
    return error
  }

  const candidate = error as { data?: unknown }

  if (candidate.data !== undefined) {
    return candidate.data
  }

  return error
}

function isOsPermissionGranted(status: PushPermissionStatus): boolean {
  return status === 'granted' || status === 'provisional'
}

function isNotFoundError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false
  }

  const candidate = error as {
    status?: number
    statusCode?: number
    response?: { status?: number }
  }

  return candidate.status === 404
    || candidate.statusCode === 404
    || candidate.response?.status === 404
}

export function usePushNotifications() {
  const config = useRuntimeConfig()
  const baseStore = useBaseStore()

  const isNative = computed(() => {
    return import.meta.client && Capacitor.isNativePlatform()
  })

  const isLoading = ref(false)
  const isSaving = ref(false)
  const permissionStatus = ref<PushPermissionStatus>('not_determined')
  const pushOptIn = ref(true)
  const categories = ref<PushCategoryPreference[]>([])
  const installationUuid = ref<string | null>(null)
  const currentFcmToken = ref<string | null>(null)

  const notificationsEnabled = computed(() => {
    return isOsPermissionGranted(permissionStatus.value) && pushOptIn.value
  })

  function normalizePreferencesResponse(
    response: PushPreferencesResponse | { success?: number, data?: PushPreferencesResponse },
  ): PushPreferencesResponse | null {
    if ('success' in response && response.success === 1 && response.data) {
      return response.data
    }

    if ('push_opt_in' in response) {
      return response
    }

    return null
  }

  async function getInstallationUuid(): Promise<string> {
    if (installationUuid.value) {
      return installationUuid.value
    }

    const stored = await Preferences.get({ key: INSTALLATION_UUID_KEY })

    if (stored.value) {
      installationUuid.value = stored.value
      return stored.value
    }

    const uuid = crypto.randomUUID()

    await Preferences.set({
      key: INSTALLATION_UUID_KEY,
      value: uuid,
    })

    installationUuid.value = uuid
    return uuid
  }

  function getDeviceMetadata(): {
    app_version: string
    os_version?: string
    device_model?: string
    locale: string
    platform: string
  } {
    let osVersion: string | undefined
    let deviceModel: string | undefined

    if (import.meta.client && typeof navigator !== 'undefined') {
      osVersion = extractOsVersionFromUserAgent(navigator.userAgent)
      deviceModel = navigator.platform
        ? truncateString(navigator.platform, DEVICE_MODEL_MAX_LENGTH)
        : undefined
    }

    return {
      app_version: baseStore.version,
      os_version: osVersion,
      device_model: deviceModel,
      locale: getPushAppLocale(),
      platform: baseStore.platform,
    }
  }

  async function registerInstallation(permissionOverride?: PushPermissionStatus): Promise<boolean> {
    const uuid = await getInstallationUuid()
    const metadata = getDeviceMetadata()

    try {
      await $fetch(`${config.public.apiUrl}/push/register-installation`, {
        method: 'POST',
        body: {
          installation_uuid: uuid,
          platform: metadata.platform,
          app_version: metadata.app_version,
          os_version: metadata.os_version,
          device_model: metadata.device_model,
          locale: metadata.locale,
          push_permission_status: normalizePermissionStatusForApi(
            permissionOverride ?? permissionStatus.value,
          ),
          push_opt_in: pushOptIn.value,
        },
      })

      return true
    }
    catch (error) {
      console.warn('Failed to register push installation:', getFetchErrorDetails(error))
      return false
    }
  }

  let registerDeviceInFlight: Promise<void> | null = null
  let registerDeviceInFlightToken: string | null = null

  async function registerDevice(token: string): Promise<void> {
    if (registerDeviceInFlight && registerDeviceInFlightToken === token) {
      return registerDeviceInFlight
    }

    registerDeviceInFlightToken = token
    registerDeviceInFlight = (async () => {
      const uuid = await getInstallationUuid()

      await $fetch(`${config.public.apiUrl}/push/register-device`, {
        method: 'POST',
        body: {
          installation_uuid: uuid,
          fcm_token: token,
          platform: baseStore.platform,
        },
      })

      currentFcmToken.value = token

      await Preferences.set({
        key: FCM_TOKEN_KEY,
        value: token,
      })
    })().finally(() => {
      if (registerDeviceInFlightToken === token) {
        registerDeviceInFlight = null
        registerDeviceInFlightToken = null
      }
    })

    return registerDeviceInFlight
  }

  async function unregisterDevice(): Promise<void> {
    const token = currentFcmToken.value || (await Preferences.get({ key: FCM_TOKEN_KEY })).value

    if (!token) {
      return
    }

    const uuid = await getInstallationUuid()

    try {
      await $fetch(`${config.public.apiUrl}/push/unregister-device`, {
        method: 'POST',
        body: {
          installation_uuid: uuid,
          fcm_token: token,
        },
      })
    }
    catch (error) {
      console.warn('Failed to unregister push device:', error)
    }

    currentFcmToken.value = null

    await Preferences.remove({ key: FCM_TOKEN_KEY })
  }

  async function fetchPreferences(): Promise<void> {
    if (!isNative.value) {
      return
    }

    isLoading.value = true

    try {
      const uuid = await getInstallationUuid()

      const response = await $fetch<PushPreferencesResponse | { success?: number, data?: PushPreferencesResponse }>(
        `${config.public.apiUrl}/push/preferences`,
        {
          method: 'GET',
          query: {
            installation_uuid: uuid,
          },
        },
      )

      const data = normalizePreferencesResponse(response)

      if (data) {
        pushOptIn.value = data.push_opt_in
        permissionStatus.value = data.push_permission_status
        categories.value = data.categories ?? []
      }
    }
    catch (error) {
      console.warn('Failed to fetch push preferences:', getFetchErrorDetails(error))
    }
    finally {
      isLoading.value = false
    }
  }

  async function updatePreferences(payload?: {
    push_opt_in?: boolean
    categories?: Record<string, boolean>
  }, allowRetry = true): Promise<boolean> {
    if (!isNative.value) {
      return false
    }

    isSaving.value = true

    try {
      const uuid = await getInstallationUuid()

      const body: {
        installation_uuid: string
        push_opt_in: boolean
        categories?: Record<string, boolean>
      } = {
        installation_uuid: uuid,
        push_opt_in: payload?.push_opt_in ?? pushOptIn.value,
      }

      if (payload?.categories !== undefined) {
        body.categories = payload.categories
      }

      const response = await $fetch<PushPreferencesResponse | { success?: number, data?: PushPreferencesResponse }>(
        `${config.public.apiUrl}/push/preferences`,
        {
          method: 'PUT',
          body,
        },
      )

      const data = normalizePreferencesResponse(response)

      if (data) {
        pushOptIn.value = data.push_opt_in
        permissionStatus.value = data.push_permission_status
        categories.value = data.categories ?? categories.value

        if (!pushOptIn.value) {
          await unregisterDevice()
        }
        else if (permissionStatus.value === 'granted') {
          await syncFcmToken()
        }

        return true
      }

      return false
    }
    catch (error) {
      if (allowRetry && isNotFoundError(error) && await registerInstallation()) {
        return updatePreferences(payload, false)
      }

      console.warn('Failed to update push preferences:', getFetchErrorDetails(error))
      return false
    }
    finally {
      isSaving.value = false
    }
  }

  async function checkPermission(): Promise<PushPermissionStatus> {
    if (!isNative.value) {
      permissionStatus.value = 'not_determined'
      return permissionStatus.value
    }

    try {
      const result = await FirebaseMessaging.checkPermissions()
      permissionStatus.value = result.receive
      return permissionStatus.value
    }
    catch (error) {
      console.warn('Failed to check push permissions:', error)
      permissionStatus.value = 'not_determined'
      return permissionStatus.value
    }
  }

  async function openSystemNotificationSettings(): Promise<void> {
    if (!isNative.value) {
      return
    }

    try {
      if (baseStore.platform === 'ios') {
        await App.openUrl({ url: 'app-settings:' })
        return
      }

      if (baseStore.platform === 'android') {
        await App.openUrl({
          url: `intent:#Intent;action=android.settings.APP_NOTIFICATION_SETTINGS;S.android.provider.extra.APP_PACKAGE=ca.glouton.app;end`,
        })
      }
    }
    catch (error) {
      console.warn('Failed to open notification settings:', error)
    }
  }

  async function requestPermission(options: { manual?: boolean } = {}): Promise<PushPermissionStatus> {
    if (!isNative.value) {
      return permissionStatus.value
    }

    if (!options.manual) {
      return checkPermission()
    }

    const currentStatus = await checkPermission()

    if (currentStatus === 'granted') {
      await syncFcmToken()
      return currentStatus
    }

    if (currentStatus === 'denied' || currentStatus === 'restricted') {
      await openSystemNotificationSettings()
      return currentStatus
    }

    try {
      const result = await FirebaseMessaging.requestPermissions()
      permissionStatus.value = result.receive
      await registerInstallation(permissionStatus.value)

      if (permissionStatus.value === 'granted') {
        await syncFcmToken()
      }

      return permissionStatus.value
    }
    catch (error) {
      console.warn('Failed to request push permissions:', error)
      return permissionStatus.value
    }
  }

  async function setupAndroidChannels(channelDefinitions?: Array<{
    id: string
    name: string
    description: string
    importance: number
  }>): Promise<void> {
    if (!isNative.value || baseStore.platform !== 'android') {
      return
    }

    const channels = channelDefinitions?.length
      ? channelDefinitions
      : [
          ...DEFAULT_ANDROID_CHANNELS,
          ...categories.value
            .filter(category => category.android_channel_id)
            .map(category => ({
              id: category.android_channel_id as string,
              name: category.label,
              description: category.description || category.label,
              importance: category.key === 'aubaines_importantes' || category.key === 'systeme' ? 4 : 3,
            })),
        ]

    const uniqueChannels = new Map(channels.map(channel => [channel.id, channel]))

    for (const channel of uniqueChannels.values()) {
      try {
        await FirebaseMessaging.createChannel({
          id: channel.id,
          name: channel.name,
          description: channel.description,
          importance: channel.importance,
          vibration: true,
          lights: true,
          visibility: 1,
        })
      }
      catch (error) {
        console.warn(`Failed to create Android notification channel ${channel.id}:`, error)
      }
    }
  }

  async function syncFcmToken(): Promise<string | null> {
    if (!isNative.value || permissionStatus.value !== 'granted' || !pushOptIn.value) {
      return null
    }

    try {
      const result = await FirebaseMessaging.getToken()
      const token = result.token

      if (token) {
        await registerDevice(token)
      }

      return token
    }
    catch (error) {
      console.warn('Failed to sync FCM token:', error)
      return null
    }
  }

  async function initialize(): Promise<void> {
    if (!isNative.value) {
      return
    }

    try {
      await getInstallationUuid()

      const storedToken = await Preferences.get({ key: FCM_TOKEN_KEY })
      currentFcmToken.value = storedToken.value

      await setupAndroidChannels()
      await checkPermission()
      await registerInstallation()
      await fetchPreferences()
      await checkPermission()

      if (permissionStatus.value === 'granted' && pushOptIn.value) {
        await syncFcmToken()
      }
    }
    catch (error) {
      console.warn('Failed to initialize push notifications:', error)
    }
  }

  async function handleUserAuthChange(isLoggedIn: boolean): Promise<void> {
    if (!isNative.value) {
      return
    }

    if (isLoggedIn) {
      await registerInstallation()
      await syncFcmToken()
      return
    }

    await unregisterDevice()
    await registerInstallation()
  }

  function loadPreviewState(): void {
    if (isNative.value) {
      return
    }

    permissionStatus.value = 'granted'
    pushOptIn.value = true
  }

  async function setNotificationsEnabled(enabled: boolean): Promise<SetNotificationsResult> {
    if (!isNative.value) {
      return 'unsupported'
    }

    if (!enabled) {
      const success = await updatePreferences({ push_opt_in: false })

      if (!success) {
        await fetchPreferences()
        return 'sync_failed'
      }

      return 'disabled'
    }

    const status = await requestPermission({ manual: true })

    if (!isOsPermissionGranted(status)) {
      return 'permission_denied'
    }

    if (!await registerInstallation(status)) {
      return 'sync_failed'
    }

    const success = await updatePreferences({ push_opt_in: true })

    if (!success) {
      await fetchPreferences()
      return 'sync_failed'
    }

    return 'enabled'
  }

  return {
    isNative,
    isLoading,
    isSaving,
    permissionStatus,
    pushOptIn,
    notificationsEnabled,
    categories,
    installationUuid,
    currentFcmToken,
    getInstallationUuid,
    registerInstallation,
    registerDevice,
    unregisterDevice,
    fetchPreferences,
    updatePreferences,
    checkPermission,
    requestPermission,
    openSystemNotificationSettings,
    setupAndroidChannels,
    syncFcmToken,
    initialize,
    handleUserAuthChange,
    loadPreviewState,
    setNotificationsEnabled,
  }
}
